import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Info, OctagonAlert } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatMoney, type AttentionItemDto, type ConsoleSummaryDto } from '@his/shared';
import { get } from '@/lib/api';
import { visibleInterval } from '@/lib/polling';
import { dateTime } from '@/lib/utils';
import { PageHeader, StatTile } from '@/components/ui/layout';
import { Button, Card, CardContent, CardHeader, CardTitle, PageSkeleton } from '@/components/ui/primitives';
import { useOwnerCapability } from './owner-nav';

interface Revenue {
  revenueByCurrency: { currency: string; mrrMinor: number; arrMinor: number }[];
  tenants: { total: number; trialing: number; active: number; pastDue: number; suspended: number };
  churnRatePct: number;
  planDistribution: { planName: string; currency: string; tenants: number; mrrMinor: number }[];
}

const TONE_ICON = { info: Info, warning: AlertTriangle, danger: OctagonAlert } as const;
const TONE_CLASS = {
  info: 'text-info',
  warning: 'text-warning',
  danger: 'text-destructive',
} as const;

function AttentionList({ items }: { items: AttentionItemDto[] }) {
  if (items.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 text-success" aria-hidden /> Nothing needs your attention right now.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border">
      {items.map((item) => {
        const Icon = TONE_ICON[item.tone];
        return (
          <li key={item.key}>
            <Link to={item.to} className="flex items-center gap-3 py-2.5 text-sm hover:underline">
              <Icon className={`h-4 w-4 shrink-0 ${TONE_CLASS[item.tone]}`} aria-hidden />
              <span className="tabular w-10 shrink-0 text-end font-semibold">{item.count}</span>
              <span>{item.label}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function RevenueSection() {
  const revenue = useQuery({ queryKey: ['platform-revenue'], queryFn: () => get<Revenue>('/platform/revenue') });
  if (revenue.isLoading) {
    return (
      <PageSkeleton />
    );
  }
  if (!revenue.data) return null;
  const data = revenue.data;
  return (
    <>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="MRR"
          value={data.revenueByCurrency.length > 0 ? data.revenueByCurrency.map((r) => formatMoney(r.mrrMinor, r.currency)).join(' · ') : formatMoney(0)}
          sub="Active subscriptions, annual plans normalised — one figure per currency, never blended"
          tone="success"
        />
        <StatTile
          label="ARR"
          value={data.revenueByCurrency.length > 0 ? data.revenueByCurrency.map((r) => formatMoney(r.arrMinor, r.currency)).join(' · ') : formatMoney(0)}
        />
        <StatTile label="Past due" value={data.tenants.pastDue} tone="warning" />
        <StatTile label="Churn" value={`${data.churnRatePct}%`} tone={data.churnRatePct > 5 ? 'warning' : 'default'} />
      </div>
      {data.planDistribution.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-sm">MRR by package</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.planDistribution.map((p) => ({
                    ...p,
                    label: data.revenueByCurrency.length === 1 ? p.planName : `${p.planName} (${p.currency})`,
                    mrr: p.mrrMinor / 100,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number, _name, props: { payload?: { currency: string } }) => formatMoney(v * 100, props.payload?.currency)} />
                  <Bar dataKey="mrr" name="MRR" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

/** Owner overview: the commercial picture of the platform (SRS 5.2). */
export function OwnerOverviewPage() {
  const canRevenue = useOwnerCapability('revenue.read');
  const summary = useQuery({
    queryKey: ['platform-summary'],
    queryFn: () => get<ConsoleSummaryDto>('/platform/summary'),
    staleTime: 60_000,
    refetchInterval: visibleInterval(60_000),
  });

  return (
    <>
      <PageHeader
        title="Home"
        description={summary.data ? `As of ${dateTime(summary.data.builtAt)}` : 'Facilities and what needs attention'}
        actions={
          <Button variant="outline" size="sm" onClick={() => void summary.refetch()} loading={summary.isFetching}>
            Refresh
          </Button>
        }
      />

      {summary.isLoading && (
        <PageSkeleton />
      )}

      {summary.data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatTile label="Facilities" value={summary.data.kpis.facilities} sub={<Link className="underline" to="/owner/tenants">Open the list</Link>} />
            <StatTile label="Live" value={summary.data.kpis.live} tone="success" />
            <StatTile label="Demo" value={summary.data.kpis.demo} />
            <StatTile label="On their own server" value={summary.data.kpis.offline} />
            <StatTile label="On trial" value={summary.data.kpis.trial} />
          </div>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-sm">Needs attention</CardTitle>
            </CardHeader>
            <CardContent>
              <AttentionList items={summary.data.attention} />
            </CardContent>
          </Card>
        </>
      )}

      {canRevenue && <RevenueSection />}
    </>
  );
}
